import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ComponentProps {
  totalPages?: number;
  currentPage: number;
  onPageChange: (i: number) => void;
}

export const Pagination: React.FC<ComponentProps> = ({
  totalPages,
  currentPage,
  onPageChange,
}) => {
  const [showInputIndex, setShowInputIndex] = useState<number|null>(null);
  const [inputValue, setInputValue] = useState<number>(currentPage);

  const renderPaginationIndex = (totalPages?: number) => {
    const indexes = [];
    let addDots = false;

    if (totalPages) {
      for (let i = 1; i <= totalPages; i++) {
        
        //Add dots between 1 and currentPage-2 if currentPage is 5 or more
        if ( i === 1 && currentPage>=5) {
          addDots = true
        }

        //Add dots between LastPage and currentPage+2 if currentPage is 3 or more away from lastPage.
        if (i===totalPages-1 && currentPage<(totalPages-3)) {
          addDots = true
        }

        //Adds Pages at FirstPage, LastPage, and Current Page +-2 
        if ( i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
          indexes.push(
            <li key={i}>
              <div
                className={`flex items-center justify-center px-4 h-10 leading-tight text-gray-500 border border-gray-300 hover:bg-gray-100 hover:text-gray-700 hover:cursor-pointer ${currentPage === i ? 'bg-ingle-light-purple text-white' : null}`}
                onClick={() => onPageChange(i)}
              >
                {i}
              </div>
            </li>
          );
        }

        //Adds '...' when needed and logic for it.
        if (addDots) {
          indexes.push(
            <li key={i}>
              <div
                className={`flex items-center justify-center px-4 h-10 leading-tight text-gray-500 border border-gray-300 hover:bg-gray-100 hover:text-gray-700 hover:cursor-pointer`}
                onClick={()=>setShowInputIndex(i)}
              >
                {showInputIndex===i ? (
                  <input
                    type="text"
                    className="w-12 text-center"
                    value={inputValue}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (!isNaN(value) && value>=0) {
                        setInputValue(value)
                      }
                    }}
                    onKeyDown={(e) => { 
                      if (e.key === 'Enter' && inputValue>0 && inputValue<=totalPages) {
                        onPageChange(inputValue)
                      }
                    }}
                    autoFocus
                    onBlur={()=>setShowInputIndex(null)}
                  />
                ) : (
                  '...'
                )}
              </div>
            </li>
          )

          addDots=false
        }
      }
    }

    return indexes;
  };


  return (
    <>
      <nav aria-label="Page Result Navigation" className="float-right">
        <ul className="flex items-center -space-x-px h-10 text-base my-4">
          <li>
            <div
              className="flex items-center justify-center px-4 h-10 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 hover:cursor-pointer"
              onClick={() => {
                const getPreviousPage = (currentPage: number): number => {
                  if (currentPage <= 1) return currentPage;
                  return currentPage - 1;
                };

                const previousPage = getPreviousPage(currentPage);

                return onPageChange(previousPage);
              }}
            >
              <span className="sr-only">Previous</span>
              <ChevronLeft
                size={20}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </div>
          </li>
          {renderPaginationIndex(totalPages)}
          <li>
            <div
              className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 hover:cursor-pointer"
              onClick={() => {
                const getNextPage = (
                  currentPage: number,
                  total?: number
                ): number => {
                  if (currentPage === total) return currentPage;
                  return currentPage + 1;
                };

                const nextPage = getNextPage(currentPage, totalPages);

                return onPageChange(nextPage);
              }}
            >
              <span className="sr-only">Next</span>
              <ChevronRight
                size={20}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </div>
          </li>
        </ul>
      </nav>
    </>
  );
};
